import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import { MessageSquare, ChevronDown, ChevronUp, FileCheck } from 'lucide-react';
import { fetchGrainSpecifications } from '../lib/sanity';
import { defaultGrainSpecifications } from '../lib/defaultContent';
import type { GrainSpecificationsContent } from '../lib/types';

const GrainSpecifications: React.FC = () => {
  const [content, setContent] = useState<GrainSpecificationsContent>(
    defaultGrainSpecifications
  );
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  useEffect(() => {
    fetchGrainSpecifications().then((data) => {
      if (data) setContent({ ...defaultGrainSpecifications, ...data });
    });
  }, []);

  const toggleRow = (index: number) => {
    setExpandedRow((prev) => (prev === index ? null : index));
  };

  return (
    <main className="pt-20">
      <Section padding="default">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-6">
            {content.title}{' '}
            <span className="text-primary-red">{content.titleHighlight}</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {content.description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
            <img
              src={content.images?.[0] || defaultGrainSpecifications.images[0]}
              alt="Grain quality inspection"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
            <img
              src={content.images?.[1] || defaultGrainSpecifications.images[1]}
              alt="Sample grains for specification testing"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Desktop Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block mb-20 overflow-x-auto rounded-2xl border border-gray-200 shadow-sm"
        >
          <table className="w-full text-sm font-inter">
            <thead>
              <tr className="bg-primary-black text-white">
                <th className="text-left px-6 py-4 font-playfair font-bold text-base">
                  Commodity
                </th>
                <th className="text-left px-6 py-4 font-semibold">
                  Moisture Max
                </th>
                <th className="text-left px-6 py-4 font-semibold">
                  Purity Min
                </th>
                <th className="text-left px-6 py-4 font-semibold">
                  Grade Standards
                </th>
                <th className="text-left px-6 py-4 font-semibold">
                  Rejection Criteria
                </th>
                <th className="text-left px-6 py-4 font-semibold">
                  Special Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {content.specs.map((spec, index) => (
                <tr
                  key={index}
                  className={`border-t border-gray-100 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-cream-50'
                  } hover:bg-primary-red/5 transition-colors`}
                >
                  <td className="px-6 py-5 font-playfair font-bold text-primary-black text-base">
                    {spec.commodity}
                  </td>
                  <td className="px-6 py-5">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-secondary-terracotta/15 text-secondary-terracotta">
                      ≤ {spec.moistureMax}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-secondary-sage/15 text-secondary-sage">
                      ≥ {spec.purityMin}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <ul className="space-y-1">
                      {spec.gradeStandards.map((grade, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-secondary-sage flex-shrink-0 mt-1.5" />
                          {grade}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-6 py-5">
                    <ul className="space-y-1">
                      {spec.rejectionCriteria.map((criterion, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-red flex-shrink-0 mt-1.5" />
                          {criterion}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-6 py-5 text-gray-600 italic leading-relaxed max-w-xs">
                    {spec.specialNotes}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Mobile Accordion Cards */}
        <div className="lg:hidden mb-20 space-y-4">
            {content.specs.map((spec, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleRow(index)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
                aria-expanded={expandedRow === index}
              >
                <span className="font-playfair font-bold text-lg text-primary-black">
                  {spec.commodity}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-secondary-terracotta bg-secondary-terracotta/10 px-2 py-1 rounded-full">
                    ≤ {spec.moistureMax} moisture
                  </span>
                  {expandedRow === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </div>
              </button>

              {expandedRow === index && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-5"
                >
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <p className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-1">
                        Moisture Max
                      </p>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-secondary-terracotta/15 text-secondary-terracotta">
                        ≤ {spec.moistureMax}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-1">
                        Purity Min
                      </p>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-secondary-sage/15 text-secondary-sage">
                        ≥ {spec.purityMin}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Grade Standards
                    </p>
                    <ul className="space-y-1.5">
                      {spec.gradeStandards.map((grade, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-secondary-sage flex-shrink-0 mt-1.5" />
                          {grade}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <p className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-2">
                      Rejection Criteria
                    </p>
                    <ul className="space-y-1.5">
                      {spec.rejectionCriteria.map((criterion, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-red flex-shrink-0 mt-1.5" />
                          {criterion}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-cream-50 rounded-lg p-3">
                    <p className="text-xs font-inter font-semibold uppercase tracking-widest text-gray-500 mb-1">
                      Special Notes
                    </p>
                    <p className="text-sm text-gray-600 italic leading-relaxed">
                      {spec.specialNotes}
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Callout Card: Contact Procurement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <Card>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-red/10 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-primary-red" />
                </div>
                <div>
                  <h3 className="text-xl font-playfair font-bold text-primary-black mb-1">
                    Have Questions About Our Specifications?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our procurement team can clarify any grading standard, testing
                    requirement, or documentation query before you prepare a shipment.
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Button variant="primary" href="/contact">
                  Contact Procurement
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* CTA: Become a Supplier */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-secondary-terracotta/20 to-secondary-sage/20 rounded-2xl p-8 md:p-12 text-center"
        >
          <div className="w-14 h-14 rounded-full bg-white/60 flex items-center justify-center mx-auto mb-4">
            <FileCheck className="w-7 h-7 text-primary-red" />
          </div>
          <h3 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-4">
            Ready to Supply?
          </h3>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto leading-relaxed">
            If your grain meets our specifications, we want to hear from you. Apply to
            become a verified Revian supplier and join our East Africa procurement
            network.
          </p>
          <Button variant="primary" href="/suppliers/become-a-supplier">
            Apply Here
          </Button>
        </motion.div>
      </Section>
    </main>
  );
};

export default GrainSpecifications;
